import { useScenarioForm } from './contexts';
import commonStyles from './common.module.scss';
import styles from './outlier-detection-configs.module.scss';
import { capitalize } from 'lodash-es';
import { isOutlierConfigModified } from './utils';
import {
  DropdownField,
  NumberField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { Button, InlineNotification } from '@carbon/react';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion,
} from 'components/accordion/accordion';
import { useEffect, useMemo, useState } from 'react';
import { Reset, Save } from '@carbon/react/icons';
import { createResourceAtom } from 'lib/atoms';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { scenarioFormDefaultValues } from './default-values';
import { ConfigSkeleton } from './config-skeleton';
import { SaveConfigModal } from './save-config-modal';
import { useSaveConfigModal } from './hooks';
import { saveOutlierConfigAtom } from './atoms';
import { OutlierConfig } from './types';

const FALLBACK_ATOM = atom(null);

export const OutlierDetectionConfigs = () => {
  const {
    form,
    defaultValues,
    selectedConfig,
    setFieldBaseline,
    updateFieldDefault,
  } = useScenarioForm();
  const [expanded, setExpanded] = useState(true);
  const [refetchOptions, setRefetchOptions] = useState<number>(0);
  const { configModalState, onClose, onOpen, error, setError } =
    useSaveConfigModal();

  const saveOutlierConfigMutation = useSetAtom(saveOutlierConfigAtom);

  const outlierConfigHref = selectedConfig?.outlierConfig?.href;
  const outlierConfigResourceHref =
    selectedConfig?.resources.outlierConfigs?.href;

  const resourceAtom = useMemo(() => {
    if (!outlierConfigHref) return FALLBACK_ATOM;

    return createResourceAtom<any>(outlierConfigHref);
  }, [outlierConfigHref]);

  const configOptionsAtom = useMemo(() => {
    if (!outlierConfigResourceHref) return FALLBACK_ATOM;

    return createResourceAtom<any>(outlierConfigResourceHref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlierConfigResourceHref, refetchOptions]);

  const configOptions = useAtomValue(configOptionsAtom);

  const formattedOptions = useMemo(() => {
    if (!(configOptions && configOptions.data?.results)) return [];

    return configOptions.data.results;
  }, [configOptions]);
  const resource = useAtomValue(resourceAtom);

  useEffect(() => {
    if (resource && resource.data) {
      const outlierConfigData = {
        id: resource.data.id,
        name: resource.data.name,
        method: resource.data.method,
        parameters: {
          ...scenarioFormDefaultValues.outlierConfig.parameters,
          ...resource.data.parameters,
        },
      };
      setFieldBaseline('outlierConfig', outlierConfigData);
    } else if (resource === null && selectedConfig?.id) {
      const fallback = scenarioFormDefaultValues.outlierConfig;
      setFieldBaseline('outlierConfig', fallback);
    }
  }, [resource, selectedConfig?.id, setFieldBaseline, form]);

  const handleSaveConfig = (name: string, isNew: boolean) => {
    const configId = form.getFieldValue('outlierConfig.id');
    const outlierConfigMethod = form.getFieldValue('outlierConfig.method');
    let url: string;
    let method: 'POST' | 'PATCH';
    if (isNew) {
      url = `/dca_scenarios/outlier_configs`;
      method = 'POST';
    } else {
      url = `/dca_scenarios/outlier_configs/${configId}`;
      method = 'PATCH';
    }

    const payload = {
      name,
      method: outlierConfigMethod!,
      parameters: {} as Record<string, any>,
    };

    if (outlierConfigMethod === 'n_sigma') {
      payload.parameters = {
        probabilityThreshold: form.getFieldValue(
          'outlierConfig.parameters.probabilityThreshold'
        ),
        numIterations: form.getFieldValue(
          'outlierConfig.parameters.numIterations'
        ),
      };
    } else if (outlierConfigMethod === 'elliptic_envelope') {
      payload.parameters = {
        contamination: form.getFieldValue(
          'outlierConfig.parameters.contamination'
        ),
      };
    }

    saveOutlierConfigMutation({
      url,
      type: method,
      data: payload,
      onSuccess: (data) => {
        onClose();
        form.setFieldValue('outlierConfig', {
          ...defaultValues?.outlierConfig,
          id: data.id,
          ...payload,
        } as OutlierConfig);
        updateFieldDefault('outlierConfig', {
          ...defaultValues?.outlierConfig,
          id: data.id,
          ...payload,
        } as OutlierConfig);
        setRefetchOptions(refetchOptions + 1);
      },
      onError: (error) => {
        if (error?.errors) {
          const errors = error.errors;
          let msg = '';
          errors.forEach((error) => {
            msg += `${error.message}`;
          });
          setError(msg);
        }
      },
    });
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <AccordionSummary>
          <h2 className={commonStyles.configFormTitle}>Outlier Detection</h2>
        </AccordionSummary>
        <AccordionDetails>
          <div className={commonStyles.configFormContainer}>
            <form.Subscribe
              selector={(state) => {
                const outlierConfig = state.values.outlierConfig;
                const isModified =
                  !!outlierConfig &&
                  !!defaultValues?.outlierConfig &&
                  isOutlierConfigModified(
                    outlierConfig,
                    defaultValues.outlierConfig
                  );

                const fieldMeta = state.fieldMeta;
                const hasErrors = (meta: { errors?: unknown[] } | undefined) =>
                  meta?.errors !== null &&
                  meta?.errors?.length &&
                  meta?.errors?.length > 0;

                let isValid = true;

                if (outlierConfig?.method === 'n_sigma') {
                  if (
                    hasErrors(
                      fieldMeta['outlierConfig.parameters.probabilityThreshold']
                    ) ||
                    hasErrors(
                      fieldMeta['outlierConfig.parameters.numIterations']
                    )
                  ) {
                    isValid = false;
                  }
                } else if (outlierConfig?.method === 'elliptic_envelope') {
                  if (
                    hasErrors(
                      fieldMeta['outlierConfig.parameters.contamination']
                    )
                  ) {
                    isValid = false;
                  }
                }

                return {
                  isModified,
                  isValid,
                  outlierConfig: state.values.outlierConfig,
                };
              }}
            >
              {({ isModified, isValid, outlierConfig }) => (
                <>
                  {!outlierConfig && <ConfigSkeleton id="Outlier Detection" />}

                  {outlierConfig && (
                    <>
                      <div className={styles.inputsContainer}>
                        <form.Field name="outlierConfig.id">
                          {(field) => (
                            <TanstackFieldWrapper<string | undefined>
                              field={field}
                            >
                              {(props) => (
                                <DropdownField
                                  id="outlierConfig.id"
                                  label="Select a config"
                                  titleText="Config (optional)"
                                  items={formattedOptions}
                                  itemToString={(item: any) =>
                                    capitalize(item.name || '')
                                  }
                                  {...props}
                                  onChange={(value: any) => {
                                    const outlierConfigData = {
                                      id: value.id,
                                      name: value.name,
                                      method: value.method,
                                      parameters:
                                        defaultValues?.outlierConfig
                                          ?.parameters,
                                    };

                                    if (value.method === 'n_sigma') {
                                      outlierConfigData.parameters = {
                                        ...outlierConfigData.parameters,
                                        probabilityThreshold:
                                          value.parameters.probabilityThreshold,
                                        numIterations:
                                          value.parameters.numIterations,
                                      };
                                    } else if (
                                      value.method === 'elliptic_envelope'
                                    ) {
                                      outlierConfigData.parameters = {
                                        ...outlierConfigData.parameters,
                                        contamination:
                                          value.parameters.contamination,
                                      };
                                    }

                                    form.setFieldValue(
                                      'outlierConfig',
                                      outlierConfigData
                                    );
                                    updateFieldDefault(
                                      'outlierConfig',
                                      outlierConfigData
                                    );
                                  }}
                                  value={
                                    outlierConfig.id
                                      ? {
                                          id: outlierConfig.id,
                                          name:
                                            formattedOptions.find(
                                              (item: any) =>
                                                item.id === outlierConfig.id
                                            )?.name ?? '',
                                        }
                                      : null
                                  }
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>
                        <form.Field name="outlierConfig.method">
                          {(field) => (
                            <TanstackFieldWrapper<string | undefined>
                              field={field}
                            >
                              {(props) => (
                                <DropdownField
                                  id="method"
                                  label="Method"
                                  titleText="Method"
                                  items={[
                                    'elliptic_envelope',
                                    'n_sigma',
                                    'none',
                                  ]}
                                  itemToString={(item: string | undefined) =>
                                    capitalize(item?.replace('_', ' ') || '')
                                  }
                                  {...props}
                                />
                              )}
                            </TanstackFieldWrapper>
                          )}
                        </form.Field>
                        {outlierConfig.method === 'n_sigma' && (
                          <>
                            <form.Field name="outlierConfig.parameters.probabilityThreshold">
                              {(field) => (
                                <TanstackFieldWrapper<number | null | undefined>
                                  field={field}
                                >
                                  {(props) => (
                                    <NumberField
                                      id="probabilityThreshold"
                                      label="Probability Threshold"
                                      {...props}
                                    />
                                  )}
                                </TanstackFieldWrapper>
                              )}
                            </form.Field>

                            <form.Field name="outlierConfig.parameters.numIterations">
                              {(field) => (
                                <TanstackFieldWrapper<number | null | undefined>
                                  field={field}
                                >
                                  {(props) => (
                                    <NumberField
                                      id="numIterations"
                                      label="Number of Iterations"
                                      {...props}
                                    />
                                  )}
                                </TanstackFieldWrapper>
                              )}
                            </form.Field>
                          </>
                        )}

                        {outlierConfig.method === 'elliptic_envelope' && (
                          <form.Field name="outlierConfig.parameters.contamination">
                            {(field) => (
                              <TanstackFieldWrapper<number | null | undefined>
                                field={field}
                              >
                                {(props) => (
                                  <NumberField
                                    id="contamination"
                                    label="Contamination"
                                    {...props}
                                  />
                                )}
                              </TanstackFieldWrapper>
                            )}
                          </form.Field>
                        )}
                      </div>

                      {isModified && (
                        <InlineNotification
                          className={commonStyles.unsavedChangesNotification}
                          lowContrast
                          hideCloseButton
                          kind="info"
                          title="Unsaved changes"
                          subtitle="Make sure to save"
                        />
                      )}

                      <div className={commonStyles.buttonContainer}>
                        <Button
                          size="md"
                          kind="tertiary"
                          onClick={() => {
                            const baseline =
                              defaultValues?.outlierConfig ?? null;
                            if (baseline === null) return;
                            form.setFieldValue('outlierConfig', baseline);
                          }}
                          renderIcon={Reset}
                          disabled={!isModified}
                        >
                          Reset
                        </Button>

                        {isModified && (
                          <Button
                            size="md"
                            kind="primary"
                            disabled={!isValid}
                            onClick={() => {
                              const isNewConfig = !outlierConfig.id;

                              const configName =
                                formattedOptions.find(
                                  (item: any) => item.id === outlierConfig?.id
                                )?.name ?? '';

                              onOpen(isNewConfig, configName);
                            }}
                            renderIcon={Save}
                          >
                            Save
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </form.Subscribe>
          </div>
        </AccordionDetails>
      </Accordion>
      {configModalState.isOpen && (
        <SaveConfigModal
          section="outlier"
          config={configModalState.config}
          defaultOption={configModalState.defaultOption}
          disableOptions={configModalState.disableOptions}
          onClose={onClose}
          error={error}
          onSave={handleSaveConfig}
        />
      )}
    </>
  );
};
